import Cookies from 'js-cookie';
import history from '__common/modules/history';
import { ActionTypes, ProjectConfigurationActionTypes } from 'actionsConstants';
import { dispatch } from '__common/store';
import { getToken, hitApi, ObservableAjax } from '__common/utils/api';
import { EMPTY, Observable, of } from 'rxjs';
import { ajax, AjaxResponse } from 'rxjs/ajax';
import pickBy from 'lodash/pickBy';
import {
  EMPTY_APP_ACTION,
  USER_LOGIN_SUCCESS,
  loginError,
  UPDATE_USER_PREFERENCES,
  REDIRECT_AURORA_PAGE,
  REDIRECT_OS_PAGE,
} from 'actions';
import { switchMap, map, catchError, withLatestFrom } from 'rxjs/operators';
import { safeOfType as ofType } from '__common/utils/safeOfType';
import { AnyAction } from 'redux';

interface AuthResponse {
  email: string;
  is_staff: boolean;
  is_privileged_user?: boolean;
  token: string;
  username: string;
  preferences: userPreferences;
  products: number[];
  permissions: string[];
}

function getApplicationListFromCookie() {
  const userDataCookie = Cookies.get('USER_DATA');
  if (!userDataCookie) {
    return {};
  }

  try {
    const userData = JSON.parse(decodeURIComponent(userDataCookie));
    return userData.application_list || {};
  } catch (e) {
    console.error('Error parsing USER_DATA cookie', e);
    return {};
  }
}

function buildLoginSuccess(response: AuthResponse) {
  return USER_LOGIN_SUCCESS(
    response.token,
    response.username,
    response.email,
    response.is_staff || false,
    response.is_privileged_user || false,
    response.products || [],
    response.permissions || [],
    getApplicationListFromCookie(),
  );
}

export function userLogin(action$: Observable<AnyAction>): Observable<AnyAction> {
  return action$.pipe(
    ofType(ActionTypes.USER_LOGIN_REQUEST),
    switchMap(({ payload: { username, password, token } }) => {
      const requestBody = {
        withCredentials: true,
        username,
        password,
        token,
      };

      return ajax<AuthResponse>(getToken('api/auth/', pickBy(requestBody))).pipe(
        map((ajaxResponse: AjaxResponse<AuthResponse>) => {
          const response = ajaxResponse.response;

          if (!token) {
            history.push('/');
          }

          Cookies.set('token', response.token, { expires: 365 });

          const notAfterSave = true;
          dispatch(UPDATE_USER_PREFERENCES(response.preferences, notAfterSave));

          return buildLoginSuccess(response);
        }),
        catchError((error) => of(loginError(error))),
      );
    }),
  );
}

export function userLogout(action$: Observable<AnyAction>): Observable<AnyAction> {
  return action$.pipe(
    ofType(ActionTypes.USER_LOGOUT_REQUEST),
    map(() => {
      Cookies.remove('token');
      Cookies.remove('_globalToken');
      return { type: ActionTypes.USER_LOGOUT_SUCCESS };
    }),
  );
}

export function registrationRequest(action$: Observable<AnyAction>): Observable<AnyAction> {
  return action$.pipe(
    ofType(ActionTypes.REGISTRATION_REQUEST),
    switchMap((action) => {
      const {
        email,
        password,
        repeatedPassword,
        postRegistrationAction,
        onErrorAction,
      } = action.payload;

      const userDetails = {
        email,
        username: email,
        password,
        password_repeat: repeatedPassword,
      };

      return ObservableAjax({
        until$: action$.pipe(ofType(ActionTypes.REGISTRATION_REQUEST)),
        nonReduxExtraAction: postRegistrationAction,
        onSuccess: EMPTY_APP_ACTION,
        onErrorAction,
        link: hitApi('post', `api/auth/register/`, userDetails),
      });
    }),
  );
}

export function auroraDesignAuthenticationRequest(
  action$: Observable<AnyAction>,
  state$: Observable<appState>,
): Observable<AnyAction> {
  return action$.pipe(
    ofType(ProjectConfigurationActionTypes.LOAD_AURORA_PROJECT_CONFIGURATION),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const token =
        action?.payload?.projectEnvConfig?.token ||
        state?.projectConfiguration?.projectEnvConfig?.token;

      // An in-progress task update has no Aurora service token. Posting
      // /api/auth/ without it is rejected as a missing username and password.
      if (!token) {
        return EMPTY;
      }

      const requestBody = {
        withCredentials: true,
        token,
      };

      return ajax<AuthResponse>(getToken('api/auth/', pickBy(requestBody))).pipe(
        map((ajaxResponse: AjaxResponse<AuthResponse>) => {
          const response = ajaxResponse.response;

          if (!token) {
            history.push('/');
          }

          Cookies.set('token', response.token, { expires: 365 });
          const notAfterSave = true;

          dispatch(UPDATE_USER_PREFERENCES(response.preferences, notAfterSave));
          dispatch(buildLoginSuccess(response));

          return REDIRECT_AURORA_PAGE();
        }),
        catchError((error) => of(loginError(error))),
      );
    }),
  );
}

export function osDesignAuthenticationRequest(
  action$: Observable<AnyAction>,
  state$: Observable<appState>,
): Observable<AnyAction> {
  return action$.pipe(
    ofType(ProjectConfigurationActionTypes.LOAD_OS_PROJECT_CONFIGURATION),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const { projectConfiguration: { projectEnvConfig: { token } } } = state;
      const requestBody = {
        withCredentials: true,
        token,
      };

      return ajax<AuthResponse>(getToken('api/auth/', pickBy(requestBody))).pipe(
        map((ajaxResponse: AjaxResponse<AuthResponse>) => {
          const response = ajaxResponse.response;

          if (!token) {
            history.push('/');
          }

          Cookies.set('token', response.token, { expires: 365 });
          const notAfterSave = true;

          dispatch(UPDATE_USER_PREFERENCES(response.preferences, notAfterSave));
          dispatch(buildLoginSuccess(response));

          return REDIRECT_OS_PAGE();
        }),
        catchError((error) => of(loginError(error))),
      );
    }),
  );
}
